import { useEffect, useState } from 'react'
import {
  DIALOG_FIELD_CLASS,
  DIALOG_FORM_STACK_CLASS,
  DialogField,
  DialogFieldRow,
} from '@/components/ui/dialog-form'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { invalidateSubscriptionCache } from '@/hooks/useOrganizationSubscription'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { getShareableWebUrl } from '@/lib/utils/app-links'
import { startProviderRedirect } from '@/lib/utils/oauth-connect'
import { isTauri } from '@/lib/utils/tauri'
import {
  formatPriceCents,
  trialApi,
  useOrganizationPricing,
} from '@/modules/subscription'
import { CreditCardIcon } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { SubscriptionPlan, WorkspaceTrialStatus } from '@scrambl/shared'
import { BillingInterval, TrialPhase } from '@scrambl/shared'

const formatMoney = (amount: number) => {
  const digits = Number.isInteger(amount) ? 0 : 2
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount)
}

/**
 * Desktop hands Checkout to the system browser and never sees the return
 * URL, so it re-reads billing state for a few minutes instead.
 */
function watchDesktopCheckout(organizationId: string): void {
  let attempts = 0
  const timer = window.setInterval(() => {
    attempts += 1
    invalidateSubscriptionCache(organizationId)
    if (attempts >= 18) window.clearInterval(timer)
  }, 10_000)
}

function isActivatablePlan(plan: SubscriptionPlan): boolean {
  return (
    plan.key !== 'free' &&
    plan.isActive !== false &&
    !plan.contactSales &&
    !plan.isComingSoon &&
    Boolean(plan.stripePriceId || plan.stripeAnnualPriceId)
  )
}

export function priceSummary(
  plan: Pick<SubscriptionPlan, 'price' | 'annualPrice'>,
  seats: number,
  interval: BillingInterval,
): string {
  if (interval === BillingInterval.YEAR) {
    const perSeat = plan.annualPrice ?? plan.price * 12
    return `${seats} × ${formatMoney(perSeat)} per seat = ${formatMoney(perSeat * seats)} per year`
  }
  return `${seats} × ${formatMoney(plan.price)} per seat = ${formatMoney(plan.price * seats)} per month`
}

/**
 * Owner/admin card activation for a no-card trial: plan, seats and billing
 * interval, then Stripe Checkout (browser handoff on desktop). While the trial
 * has time left the remaining days are kept and billing starts at trial end.
 */
export function TrialActivationDialog({
  open,
  onOpenChange,
  organizationId,
  trial,
  currentSeats,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string
  trial: WorkspaceTrialStatus
  currentSeats: number
}) {
  const { formatDate } = useUserTimezone()
  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['subscription-plans', 'activatable'],
    queryFn: trialApi.plans,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  })
  const activatable = plans.filter(isActivatablePlan)
  const [planKey, setPlanKey] = useState(trial.planKey)
  const [interval, setBillingInterval] = useState<BillingInterval>(
    BillingInterval.MONTH,
  )
  const [seatsInput, setSeatsInput] = useState(String(currentSeats || 1))
  const [isSubmitting, setIsSubmitting] = useState(false)

  const plan =
    activatable.find(candidate => candidate.key === planKey) ?? activatable[0]
  // The price Checkout will bill: this organization's custom price when it
  // has one (docs/PLAN-pricing-overrides.md).
  const { priceFor } = useOrganizationPricing(organizationId)
  const priceView = plan
    ? priceFor(
        plan.id,
        plan.stripeAnnualPriceId && interval === BillingInterval.YEAR
          ? 'year'
          : 'month',
      )
    : null
  const minSeats = priceView?.minSeats ?? plan?.minSeats ?? 1
  const parsedSeats = Number(seatsInput)
  const hasValidSeatCount =
    seatsInput.trim() !== '' &&
    Number.isSafeInteger(parsedSeats) &&
    parsedSeats >= minSeats
  const seats = hasValidSeatCount ? parsedSeats : minSeats
  useEffect(() => {
    setSeatsInput(value => {
      const count = Number(value)
      return Number.isSafeInteger(count) && count >= minSeats
        ? value
        : String(minSeats)
    })
  }, [plan?.id, minSeats])
  const hasAnnual = Boolean(plan?.stripeAnnualPriceId)
  const effectiveInterval = hasAnnual ? interval : BillingInterval.MONTH

  const waitForTrialEnd =
    trial.billingRequiresTrialEnd &&
    trial.phase === TrialPhase.TRIALING &&
    !trial.canPreserveTrial
  const timing = waitForTrialEnd
    ? `Your trial remains free through ${formatDate(trial.trialEndsAt, 'MMMM d')}. Return here after it ends to choose a paid plan.`
    : trial.canPreserveTrial
      ? `You won't be charged until your trial ends on ${formatDate(trial.trialEndsAt, 'MMMM d')}.`
      : trial.phase === TrialPhase.LOCKED
        ? "You'll be charged today and editing turns back on right away."
        : "You'll be charged today."

  const handleSubmit = async () => {
    if (!plan || !hasValidSeatCount || waitForTrialEnd) return
    setIsSubmitting(true)
    try {
      const session = await trialApi.activate(organizationId, {
        returnUrl: getShareableWebUrl(),
        planId: plan.id,
        seats,
        billingInterval: effectiveInterval,
      })
      if (!session.url) throw new Error('Checkout did not open')
      await startProviderRedirect(session.url)
      if (isTauri()) watchDesktopCheckout(organizationId)
      onOpenChange(false)
    } catch (error) {
      toast.error('Could not open checkout', {
        description: error instanceof Error ? error.message : undefined,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <DialogPattern
      open={open}
      onOpenChange={onOpenChange}
      title="Activate Scrambl"
      description={timing}
      icon={<CreditCardIcon className="h-4 w-4 text-primary" />}
      onSubmit={handleSubmit}
      submitLabel="Continue to payment"
      isLoading={isLoading}
      isSubmitting={isSubmitting}
      submitDisabled={!plan || !hasValidSeatCount || Boolean(waitForTrialEnd)}
    >
      <div className={DIALOG_FORM_STACK_CLASS}>
        <DialogFieldRow>
          <DialogField label="Plan" htmlFor="trial-activation-plan">
            <Select value={plan?.key ?? planKey} onValueChange={setPlanKey}>
              <SelectTrigger
                id="trial-activation-plan"
                className={DIALOG_FIELD_CLASS}
              >
                <SelectValue placeholder="Choose a plan" />
              </SelectTrigger>
              <SelectContent>
                {activatable.map(candidate => (
                  <SelectItem key={candidate.key} value={candidate.key}>
                    {candidate.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DialogField>
          <DialogField label="Billing" htmlFor="trial-activation-interval">
            <Select
              value={effectiveInterval}
              onValueChange={value =>
                setBillingInterval(value as BillingInterval)
              }
              disabled={!hasAnnual}
            >
              <SelectTrigger
                id="trial-activation-interval"
                className={DIALOG_FIELD_CLASS}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BillingInterval.MONTH}>Monthly</SelectItem>
                <SelectItem value={BillingInterval.YEAR}>Annual</SelectItem>
              </SelectContent>
            </Select>
          </DialogField>
        </DialogFieldRow>
        <DialogField
          label="Seats"
          htmlFor="trial-activation-seats"
          error={
            !hasValidSeatCount
              ? `Enter a whole number of seats, at least ${minSeats}.`
              : undefined
          }
          hint={
            minSeats > 1
              ? `${plan?.displayName ?? 'This plan'} starts at ${minSeats} seats.`
              : undefined
          }
        >
          <Input
            id="trial-activation-seats"
            type="number"
            inputMode="numeric"
            min={minSeats}
            value={seatsInput}
            aria-invalid={!hasValidSeatCount}
            onChange={event => setSeatsInput(event.target.value)}
            onBlur={() => {
              if (hasValidSeatCount) setSeatsInput(String(parsedSeats))
            }}
            className={DIALOG_FIELD_CLASS}
          />
        </DialogField>
        {plan && (
          <p
            className="text-sm text-foreground"
            data-testid="trial-activation-summary"
          >
            {priceSummary(
              priceView
                ? effectiveInterval === BillingInterval.YEAR
                  ? {
                      price: plan.price,
                      annualPrice: priceView.effectiveUnitAmount / 100,
                    }
                  : {
                      price: priceView.effectiveUnitAmount / 100,
                      annualPrice: plan.annualPrice,
                    }
                : plan,
              seats,
              effectiveInterval,
            )}
          </p>
        )}
        {priceView?.source === 'override' ? (
          <p className="text-xs text-muted-foreground">
            {priceView.overrideLabel ?? 'Custom'} pricing · standard{' '}
            <span className="line-through">
              {formatPriceCents(priceView.standardUnitAmount)}
            </span>{' '}
            per seat
          </p>
        ) : null}
      </div>
    </DialogPattern>
  )
}
